import { useState, useCallback, useEffect } from "react";

import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
} from "@material-ui/core";

import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import StructureFields from "components/_refactor/fields/structure";
import useForm from "hooks/form";
import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import ClearButton from "components/_refactor/button/clear";
import LoadingData from "components/Loading/LoadingData";
import useLogs from "hooks/logs";
import useStructureQueries from "hooks/useStructureQueries";

import TableTermosAdesao from "./table/TableTermosAdesao";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import { IsMobile900, IsMobile } from "utils/is-mobile";

const situationOptions = [
  {
    value: "ADERIDO",
    name: "Aderido",
  },
  {
    value: "FALTAADERIR",
    name: "Falta aderir à atualização",
  },
  {
    value: "NAOADERIDO",
    name: "Não aderido",
  },
  {
    value: "RENUNCIADO",
    name: "Renunciado",
  },
  {
    value: "TODOS",
    name: "Todos",
  },
];

function GerenciamentoTermosAdesao() {
  const snackbar = useSnackbar();
  const { setLocalStorageActions, setLocalStorageApiProblem } = useLogs();

  const [searching, setSearching] = useState(false);
  const [lojas, setLojas] = useState(null);
  const [structureFilters, setStructureFilters] = useState({});
  const [structureLoading, setStructureLoading] = useState([]);
  const [opcoesModalidades, setOpcoesModalidades] = useState(null);
  const [situation, setSituation] = useState("TODOS");
  const [hasUpdated, setHasUpdated] = useState(false);
  const [clearFilters, setClearFilters] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [hasManyRegisters, setHasManyRegisters] = useState(false);
  const [firstSearch, setFirstSearch] = useState(false);
  const [hasNoBrand, setHasNoBrand] = useState(false);
  const [hasNoGroup, setHasNoGroup] = useState(false);

  const [totalRegisters, setTotalRegisters] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(25);

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();

  const itemXS = isMobile ? 12 : isMobile900 ? 6 : 4;

  const { values, handleChange, errors } = useForm({
    idModalidade: "Todos",
  });

  const updateList = useCallback(
    async (updatedModalidade) => {
      const newModalidadeList = lojas?.map((modalidade) => {
        if (
          modalidade?.IdModalidade === updatedModalidade?.IdModalidade &&
          modalidade.IdPessoaLoja === updatedModalidade.IdPessoaLoja
        ) {
          setHasUpdated(true);
          return updatedModalidade;
        } else {
          return modalidade;
        }
      });

      setLojas(newModalidadeList);
    },
    [lojas]
  );

  const { getSalesMode } = useStructureQueries();

  const getOpcoesModalidades = useCallback(async () => {
    try {
      const result = await getSalesMode({ ativo: true });
      setOpcoesModalidades(result?.data ?? []);
    } catch (e) {
      console.log(e);
      snackbar.showMessage(
        "Ocorreu um erro ao buscar lojas de vendas. Tente novamente mais tarde.",
        {
          severity: "error",
        }
      );
      setLocalStorageApiProblem({
        idMenuAccess: "128",
        screen: "Gerenciamento Termos de Adesão",
        problem: `Erro na chamada do endpoint sales-mode - ${e}`,
      });
    }
  }, [snackbar, getSalesMode]);

  const searchTerms = async ({ page = 1, sizePerPage = 25 }) => {
    setCancelLoading(false);
    setSearching(true);
    setHasUpdated(false);
    setNotFound(false);
    setHasManyRegisters(false);

    await Api.get(
      "list-sales-mode-active-to-accept",
      "storeadditionalinfo",
      UtilsFncs.RestParamsComposer(
        structureFilters,
        "?idModalidade=" +
          (values?.idModalidade === "Todos" ? "" : values?.idModalidade) +
          "&situacao=" +
          (situation === "TODOS" ? "" : situation) +
          "&idMarca=TODAS" +
          `&page=${page - 1}` +
          `&pageSize=${sizePerPage ? sizePerPage : 25}`
      ),
      true
    )
      .then((res) => {
        if (res.errorMessage || res.cause) {
          if (
            res.cause?.errorMessage ===
            "Unable to expand length of this stream beyond its capacity."
          ) {
            setHasManyRegisters(true);
          } else {
            setNotFound(true);
          }
        } else {
          setLojas(res?.Result?.ListTermos || []);
          setTotalRegisters(res?.Result?.TotalRegistros);
          setCurrentPage(page);
          setCurrentSizePerPage(sizePerPage);
        }
      })
      .catch((e) => {
        console.log(e);
        setNotFound(true);
        setLocalStorageApiProblem({
          idMenuAccess: "128",
          screen: "Gerenciamento Termos de Adesão",
          problem: `Erro na chamada do endpoint list-sales-mode-active-to-accept - ${e}`,
        });
      })
      .finally(() => setSearching(false));
  };

  const handleClearFilters = () => {
    setClearFilters(true);
    setSearching(true);
    handleChange({
      target: {
        name: "idModalidade",
        value: "Todos",
      },
    });
    setSituation("TODOS");
    setSearching(false);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    setLojas(null);
  };

  const handleVerifyRequiredInputs = () => {
    if (structureFilters.idGrupoEconomico) {
      setLocalStorageActions({
        nomeTela: "Gerenciamento Termos de Adesão",
        idMenu: "128",
        acao: "Clicou para Pesquisar",
      });
      return searchTerms({
        page: 1,
        sizePerPage: currentSizePerPage,
      });
    } else {
      if (!structureFilters.idGrupoEconomico) {
        setHasNoGroup(true);
      }
    }
  };

  useEffect(() => {
    if (notFound && !cancelLoading) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar os termos de adesão. Verifique os parâmetros informados e/ou tente novamente mais tarde.",
        {
          severity: "error",
        }
      );
    }
  }, [notFound, cancelLoading]);

  useEffect(() => {
    if (hasManyRegisters && !cancelLoading) {
      snackbar.showMessage(
        "A busca com os parâmetros utilizados encontrou muitos registros. Simplifique sua busca utilizando os filtros disponíveis e tente novamente.",
        {
          severity: "info",
          autoHideDuration: 10000,
        }
      );
    }
  }, [hasManyRegisters, cancelLoading]);

  useEffect(() => {
    if (structureFilters?.idMarca) {
      setHasNoBrand(false);
    }
    if (structureFilters?.idGrupoEconomico) {
      setHasNoGroup(false);
    }

    if (
      (structureFilters?.idMarca === null &&
        structureFilters?.idCanal === "" &&
        structureFilters?.idRepresentante === "" &&
        structureFilters?.idGrupoEconomico === "") ||
      values.idModalidade === "Todos"
    ) {
      setClearFilters(false);
    }
  }, [structureFilters, values]);

  useEffect(() => {
    if (hasUpdated) {
      searchTerms({
        page: 1,
        sizePerPage: currentSizePerPage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUpdated]);

  useEffect(() => {
    if (
      structureFilters.idMarca &&
      structureFilters.idGrupoEconomico &&
      !firstSearch
    ) {
      setFirstSearch(true);
      searchTerms({
        page: 1,
        sizePerPage: currentSizePerPage,
      });
    }
  }, [structureFilters.idMarca, structureFilters.idGrupoEconomico]);

  useEffect(() => {
    getOpcoesModalidades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <HeaderClientes section="Termos de Adesão" subsection="Gerenciamento" />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Filtros"
                titleTypographyProps={{
                  component: Box,
                  marginBottom: "0!important",
                  variant: "h3",
                }}
              ></CardHeader>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <StructureFields
                    clearFilters={clearFilters}
                    onStructureFieldsChange={setStructureFilters}
                    disableAll={searching}
                    onStructureFieldsLoad={setStructureLoading}
                    fieldsOrder={["economicGroup", "store"]}
                    requiredFields={["economicGroup"]}
                    hasNoBrand={hasNoBrand}
                    hasNoGroup={hasNoGroup}
                    hideFields={["channel", "brand"]}
                  />
                  <Grid item xs={12}>
                    <Divider style={{ margin: "0.5rem 0" }} />
                  </Grid>
                  <Grid item xs={itemXS}>
                    <AutoCompleteField
                      label="Modalidade de Venda"
                      required
                      options={
                        opcoesModalidades
                          ? [
                              ...opcoesModalidades,
                              { Id: "Todos", Nome: "Todos" },
                            ]
                          : [{ Id: "Todos", Nome: "Todos" }]
                      }
                      optionLabelKey="Nome"
                      optionIdKey="Id"
                      value={
                        (opcoesModalidades || []).find(
                          (item) => item.Id === values.idModalidade
                        ) ||
                        (values.idModalidade === "Todos"
                          ? { Id: "Todos", Nome: "Todos" }
                          : null)
                      }
                      disabled={!opcoesModalidades || searching}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: "idModalidade",
                            value: newValue ? newValue.Id : "Todos",
                          },
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={itemXS}>
                    <AutoCompleteField
                      label="Situação"
                      required
                      options={situationOptions}
                      optionLabelKey="name"
                      optionIdKey="value"
                      value={
                        situationOptions.find(
                          (item) => item.value === situation
                        ) || null
                      }
                      disabled={!opcoesModalidades || searching}
                      onChange={(newValue) => {
                        setSituation(newValue ? newValue.value : "");
                      }}
                    />
                  </Grid>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={isMobile ? 12 : isMobile900 && 6}>
                      <Button
                        style={
                          isMobile || isMobile900
                            ? {
                                width: "100%",
                                marginBottom: "0.75rem",
                                marginTop: "0.75rem",
                              }
                            : { marginBottom: "0rem" }
                        }
                        variant="contained"
                        onClick={handleVerifyRequiredInputs}
                        disabled={
                          searching ||
                          Object.keys(errors).length > 0 ||
                          structureLoading.length > 0
                        }
                      >
                        {searching ? "Pesquisando..." : "Pesquisar"}
                      </Button>
                    </Grid>
                    <Grid item xs={isMobile ? 12 : isMobile900 && 6}>
                      <ClearButton
                        fullWidth={true}
                        disable={searching}
                        onClick={() => handleClearFilters()}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <LoadingData
              searching={searching}
              onClick={() => handleStopSearching()}
              label="Buscando dados..."
            />
          </Grid>
          {!searching && lojas && !cancelLoading ? (
            <TableTermosAdesao
              dataTable={lojas}
              updateList={updateList}
              totalRegistros={totalRegisters}
              currentPage={currentPage}
              currentSizePerPage={currentSizePerPage}
              search={searchTerms}
            />
          ) : null}
        </Grid>
      </Container>
    </>
  );
}

export default GerenciamentoTermosAdesao;
